import React, { useCallback, useState } from 'react';
import { Box, Dialog, IconButton, Menu, MenuItem, Typography } from '@mui/material';
import {
  DragDropContext,
  Droppable,
  Draggable,
  DraggableProvided,
  DraggableStateSnapshot,
  DroppableProvided,
  DroppableStateSnapshot
} from 'react-beautiful-dnd';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { ModuleStructure, GeneratedModule, LessonStatus, ModuleStatus } from '../types';
import { useStructureDnD } from '../hooks/useStructureDnD';
import { displayStructureTitle } from '../utils/structureTitle';
import { ctaButtonSx, secondaryButtonSx } from '../../../../../components/ui/Buttons/buttonStyles';

// Names wrap in full rather than being cut with an ellipsis.
const titleSx = { fontFamily: 'Inter', overflowWrap: 'anywhere' } as const;
const actionIconSx = { fontSize: 15, color: 'var(--Stroke-highlight, #F79420)' };

interface ModuleSidebarProps {
  programName: string;
  modules: ModuleStructure[];
  selectedModuleIdx: number | null;
  selectedLessonIdx: number | null;
  onSelectModule: (idx: number) => void;
  onSelectLesson: (moduleIdx: number, lessonIdx: number) => void;
  onReorder: (modules: ModuleStructure[]) => void;
  onRenameModule: (idx: number, title: string) => void;
  onRenameLesson: (moduleIdx: number, lessonIdx: number, title: string) => void;
  onAddModule: () => void;
  onAddLesson: (moduleIdx: number) => void;
  onDeleteModule: (idx: number) => void;
  onDeleteLesson: (moduleIdx: number, lessonIdx: number) => void;
  onReasoningClick: (moduleIdx: number) => void;
  onImproveModuleClick?: (moduleIdx: number) => void;
  onModuleDueDate?: (idx: number) => void;
  onProgramNameChange?: (name: string) => void;
  /** Show the per-module "AI Reasoning" star. Default true (the wizard). The
   *  post-creation design editor has no reasoning data, so it passes false. */
  showReasoning?: boolean;
  readOnly?: boolean;
  generatedModules?: GeneratedModule[];
  pendingMediaLessons?: Set<string>;
  // Hide the program-name box (shown in the modal header instead when embedded).
  hideProgramName?: boolean;
  /** A line under a lesson's title (the build flow: what the lesson is for). */
  renderLessonExtra?: (moduleIdx: number, lessonIdx: number) => React.ReactNode;
}

const ModuleSidebar: React.FC<ModuleSidebarProps> = ({
  programName,
  hideProgramName = false,
  modules,
  selectedModuleIdx,
  selectedLessonIdx,
  onSelectModule,
  onSelectLesson,
  onReorder,
  onRenameModule,
  onRenameLesson,
  onAddModule,
  onAddLesson,
  onDeleteModule,
  onDeleteLesson,
  onReasoningClick,
  onImproveModuleClick,
  onModuleDueDate,
  onProgramNameChange,
  showReasoning = true,
  readOnly = false,
  generatedModules,
  pendingMediaLessons,
  renderLessonExtra
}) => {
  const { handleDragEnd } = useStructureDnD(modules, onReorder);

  // Module menu
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuModuleIdx, setMenuModuleIdx] = useState<number | null>(null);

  // Lesson menu
  const [lessonMenuAnchor, setLessonMenuAnchor] = useState<null | HTMLElement>(null);
  const [lessonMenuTarget, setLessonMenuTarget] = useState<{ m: number; l: number } | null>(null);

  // Rename modal
  const [renameModal, setRenameModal] = useState<{
    type: 'module' | 'lesson';
    mIdx: number;
    lIdx?: number;
    currentName: string;
  } | null>(null);
  const [renameValue, setRenameValue] = useState('');

  const openRenameModal = useCallback(
    (type: 'module' | 'lesson', mIdx: number, lIdx?: number, currentName?: string) => {
      setRenameValue(currentName || '');
      setRenameModal({ type, mIdx, lIdx, currentName: currentName || '' });
    },
    []
  );

  const handleRenameSubmit = useCallback(() => {
    if (!renameModal || !renameValue.trim()) return;
    if (renameModal.type === 'module') {
      onRenameModule(renameModal.mIdx, renameValue.trim());
    } else if (renameModal.lIdx !== undefined) {
      onRenameLesson(renameModal.mIdx, renameModal.lIdx, renameValue.trim());
    }
    setRenameModal(null);
    setRenameValue('');
  }, [renameModal, renameValue, onRenameModule, onRenameLesson]);

  const getLessonStatus = (moduleIdx: number, lessonIdx: number): LessonStatus | null => {
    if (pendingMediaLessons?.has(`${moduleIdx}-${lessonIdx}`)) return 'generating';
    if (!generatedModules) return null;
    return generatedModules[moduleIdx]?.lessons?.[lessonIdx]?.status || null;
  };

  const getModuleStatus = (moduleIdx: number): ModuleStatus | null => {
    if (!generatedModules) return null;
    return generatedModules[moduleIdx]?.status || null;
  };

  const renderStatusIcon = (status: LessonStatus | ModuleStatus | null) => {
    if (status === 'generating')
      return (
        <Box
          sx={{
            width: 18,
            height: 18,
            borderRadius: '50%',
            flexShrink: 0,
            border: '2px dashed var(--Text-text-tertiary, #868686)',
            animation: 'spin 2s linear infinite',
            '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } }
          }}
        />
      );
    if (status === 'pending')
      return (
        <Box
          sx={{
            width: 18,
            height: 18,
            borderRadius: '50%',
            flexShrink: 0,
            border: '2px dashed var(--Text-text-tertiary, #868686)',
            opacity: 0.4
          }}
        />
      );
    if (status === 'completed')
      return <CheckCircleOutlineIcon sx={{ fontSize: 18, color: 'var(--UI-Success, #4CAF50)', flexShrink: 0 }} />;
    if (status === 'failed')
      return (
        <Box
          sx={{
            width: 18,
            height: 18,
            borderRadius: '50%',
            flexShrink: 0,
            border: '2px solid var(--UI-Error, #F44336)'
          }}
        />
      );
    return null;
  };

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 1, pt: hideProgramName ? '16px' : 0 }}>
      {/* Program name — hidden when embedded (the modal header shows it). */}
      {!hideProgramName &&
        (onProgramNameChange ? (
          <Box
            component="input"
            value={programName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onProgramNameChange(e.target.value)}
            sx={{
              background: '#0F0F0F',
              border: '1px solid var(--Stroke-Border-border-dim, #2A2A2A)',
              borderRadius: '8px',
              p: 1.5,
              color: 'var(--Text-text-primary, #FDFDFD)',
              fontFamily: 'Inter',
              fontSize: 14,
              fontWeight: 500,
              outline: 'none',
              mb: 2,
              '&:focus': { borderColor: 'var(--Stroke-highlight, #F79420)' }
            }}
          />
        ) : (
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 500,
              fontFamily: 'Inter',
              color: 'var(--Text-text-primary, #FDFDFD)',
              mb: 2
            }}
          >
            {programName}
          </Typography>
        ))}

      {/* Modules header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography
          sx={{ fontSize: 13, fontWeight: 600, fontFamily: 'Inter', color: 'var(--Text-text-secondary, #B6B6B6)' }}
        >
          Modules
        </Typography>
        {!readOnly && (
          <IconButton size="small" onClick={onAddModule} sx={{ color: 'var(--Text-text-secondary, #B6B6B6)' }}>
            <AddIcon sx={{ fontSize: 18 }} />
          </IconButton>
        )}
      </Box>

      {/* DnD Module list */}
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="modules" type="MODULE" isDropDisabled={readOnly}>
          {(provided: DroppableProvided, snapshot: DroppableStateSnapshot) => (
            <Box
              ref={provided.innerRef}
              {...provided.droppableProps}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
                transition: 'background 0.2s',
                background: snapshot.isDraggingOver ? 'rgba(247,148,32,0.04)' : 'transparent',
                borderRadius: '8px',
                minHeight: 20
              }}
            >
              {modules.map((mod, mIdx) => {
                const isSelectedModule = selectedModuleIdx === mIdx;

                return (
                  <Draggable
                    key={`module-${mIdx}`}
                    draggableId={`module-${mIdx}`}
                    index={mIdx}
                    isDragDisabled={readOnly}
                  >
                    {(modProvided: DraggableProvided, modSnapshot: DraggableStateSnapshot) => (
                      <Box
                        ref={modProvided.innerRef}
                        {...modProvided.draggableProps}
                        sx={{
                          mb: 0.5,
                          borderRadius: '8px',
                          transition: 'box-shadow 0.2s, transform 0.15s',
                          boxShadow: modSnapshot.isDragging ? '0 8px 24px rgba(0,0,0,0.5)' : 'none',
                          background: modSnapshot.isDragging ? '#1F1F1F' : 'transparent',
                          zIndex: modSnapshot.isDragging ? 10 : 'auto'
                        }}
                      >
                        {/* Module row */}
                        <Box
                          onClick={() => onSelectModule(mIdx)}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            p: 1,
                            borderRadius: '6px',
                            cursor: 'pointer',
                            background:
                              isSelectedModule && selectedLessonIdx === null
                                ? 'rgba(247, 148, 32, 0.1)'
                                : 'transparent',
                            borderLeft:
                              isSelectedModule && selectedLessonIdx === null
                                ? '2px solid var(--Stroke-highlight, #F79420)'
                                : '2px solid transparent',
                            '&:hover': { background: 'rgba(255,255,255,0.04)' }
                          }}
                        >
                          {!readOnly && (
                            <Box
                              {...modProvided.dragHandleProps}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                cursor: 'grab',
                                '&:active': { cursor: 'grabbing' }
                              }}
                            >
                              <DragIndicatorIcon sx={{ fontSize: 16, color: 'var(--Text-text-tertiary, #868686)' }} />
                            </Box>
                          )}

                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              sx={{ fontSize: 11, fontFamily: 'Inter', color: 'var(--Text-text-tertiary, #868686)' }}
                            >
                              Module {mIdx + 1}
                            </Typography>
                            <Typography
                              sx={{ ...titleSx, fontSize: 13, fontWeight: 600, color: 'var(--Text-text-primary, #FDFDFD)' }}
                            >
                              {displayStructureTitle(mod.title)}
                            </Typography>
                          </Box>

                          {renderStatusIcon(getModuleStatus(mIdx))}

                          {!readOnly && (
                            <>
                              {showReasoning && (
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onReasoningClick(mIdx);
                                  }}
                                  title="AI Reasoning"
                                  sx={{ p: 0 }}
                                >
                                  <InfoOutlinedIcon sx={actionIconSx} />
                                </IconButton>
                              )}
                              {onImproveModuleClick && (
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onImproveModuleClick(mIdx);
                                  }}
                                  title="Improve with Elliott"
                                  sx={{ p: 0 }}
                                >
                                  <EditOutlinedIcon sx={actionIconSx} />
                                </IconButton>
                              )}
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setMenuAnchor(e.currentTarget);
                                  setMenuModuleIdx(mIdx);
                                }}
                                sx={{ p: 0.25 }}
                              >
                                <MoreVertIcon sx={{ fontSize: 16, color: 'var(--Text-text-tertiary, #868686)' }} />
                              </IconButton>
                            </>
                          )}
                        </Box>

                        {/* Lesson list (droppable) */}
                        <Droppable droppableId={`lessons-${mIdx}`} type="LESSON" isDropDisabled={readOnly}>
                          {(lessonProvided: DroppableProvided, lessonSnapshot: DroppableStateSnapshot) => (
                            <Box
                              ref={lessonProvided.innerRef}
                              {...lessonProvided.droppableProps}
                              sx={{
                                minHeight: 4,
                                transition: 'background 0.2s',
                                background: lessonSnapshot.isDraggingOver ? 'rgba(247,148,32,0.06)' : 'transparent',
                                borderRadius: '4px',
                                ml: readOnly ? 0 : 1
                              }}
                            >
                              {mod.lessons.map((lesson, lIdx) => {
                                const isSelected = selectedModuleIdx === mIdx && selectedLessonIdx === lIdx;
                                const status = getLessonStatus(mIdx, lIdx);

                                return (
                                  <Draggable
                                    key={`lesson-${mIdx}-${lIdx}`}
                                    draggableId={`lesson-${mIdx}-${lIdx}`}
                                    index={lIdx}
                                    isDragDisabled={readOnly}
                                  >
                                    {(lProvided: DraggableProvided, lSnapshot: DraggableStateSnapshot) => (
                                      <Box
                                        ref={lProvided.innerRef}
                                        {...lProvided.draggableProps}
                                        onClick={() => onSelectLesson(mIdx, lIdx)}
                                        sx={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: 0.5,
                                          pl: readOnly ? 3 : 2.5,
                                          pr: 1,
                                          py: 0.75,
                                          cursor: 'pointer',
                                          borderRadius: '4px',
                                          background: lSnapshot.isDragging
                                            ? '#252525'
                                            : isSelected
                                            ? 'rgba(247, 148, 32, 0.08)'
                                            : 'transparent',
                                          transition: 'background 0.15s, box-shadow 0.2s',
                                          boxShadow: lSnapshot.isDragging ? '0 4px 16px rgba(0,0,0,0.4)' : 'none',
                                          '&:hover': {
                                            background: lSnapshot.isDragging ? '#252525' : 'rgba(255,255,255,0.03)'
                                          }
                                        }}
                                      >
                                        {!readOnly && (
                                          <Box
                                            {...lProvided.dragHandleProps}
                                            sx={{
                                              display: 'flex',
                                              alignItems: 'center',
                                              cursor: 'grab',
                                              '&:active': { cursor: 'grabbing' }
                                            }}
                                          >
                                            <DragIndicatorIcon
                                              sx={{ fontSize: 14, color: 'var(--Text-text-tertiary, #868686)' }}
                                            />
                                          </Box>
                                        )}

                                        <Typography
                                          sx={{
                                            fontSize: 12,
                                            fontFamily: 'Inter',
                                            color: 'var(--Text-text-tertiary, #868686)',
                                            mr: 0.5,
                                            flexShrink: 0
                                          }}
                                        >
                                          {lIdx + 1}
                                        </Typography>

                                        {/* With a line under it (renderLessonExtra), the title and the line share the space. */}
                                        <Box
                                          sx={renderLessonExtra ? { flex: 1, minWidth: 0 } : { display: 'contents' }}
                                        >
                                          <Typography
                                            sx={{
                                              ...titleSx,
                                              flex: 1,
                                              minWidth: 0,
                                              fontSize: 12,
                                              color: isSelected
                                                ? 'var(--Stroke-highlight, #F79420)'
                                                : 'var(--Text-text-primary, #FDFDFD)'
                                            }}
                                          >
                                            {displayStructureTitle(lesson.title)}
                                          </Typography>
                                          {renderLessonExtra?.(mIdx, lIdx)}
                                        </Box>

                                        {renderStatusIcon(status)}

                                        {!readOnly && (
                                          <IconButton
                                            size="small"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setLessonMenuAnchor(e.currentTarget);
                                              setLessonMenuTarget({ m: mIdx, l: lIdx });
                                            }}
                                            sx={{ p: 0.25, opacity: 0.5, '&:hover': { opacity: 1 } }}
                                          >
                                            <MoreVertIcon
                                              sx={{ fontSize: 14, color: 'var(--Text-text-tertiary, #868686)' }}
                                            />
                                          </IconButton>
                                        )}
                                      </Box>
                                    )}
                                  </Draggable>
                                );
                              })}
                              {lessonProvided.placeholder}
                            </Box>
                          )}
                        </Droppable>

                        {/* Add lesson */}
                        {!readOnly && (
                          <Box
                            onClick={() => onAddLesson(mIdx)}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              pl: 4,
                              py: 0.5,
                              cursor: 'pointer',
                              '&:hover': { opacity: 0.8 }
                            }}
                          >
                            <Typography
                              sx={{ fontSize: 11, fontFamily: 'Inter', color: 'var(--Text-text-tertiary, #868686)' }}
                            >
                              Add Lesson +
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    )}
                  </Draggable>
                );
              })}
              {provided.placeholder}
            </Box>
          )}
        </Droppable>
      </DragDropContext>

      {/* Module context menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => {
          setMenuAnchor(null);
          setMenuModuleIdx(null);
        }}
        PaperProps={{ sx: { background: '#1A1A1A', border: '1px solid var(--Stroke-Border-border-dim, #2A2A2A)' } }}
      >
        <MenuItem
          onClick={() => {
            if (menuModuleIdx !== null)
              openRenameModal('module', menuModuleIdx, undefined, modules[menuModuleIdx]?.title);
            setMenuAnchor(null);
            setMenuModuleIdx(null);
          }}
          sx={{ fontSize: 13, fontFamily: 'Inter', color: 'var(--Text-text-primary, #FDFDFD)' }}
        >
          Rename
        </MenuItem>
        {onImproveModuleClick && (
          <MenuItem
            onClick={() => {
              if (menuModuleIdx !== null) onImproveModuleClick(menuModuleIdx);
              setMenuAnchor(null);
              setMenuModuleIdx(null);
            }}
            sx={{ fontSize: 13, fontFamily: 'Inter', color: 'var(--Stroke-highlight, #F79420)' }}
          >
            Improve with Elliott
          </MenuItem>
        )}
        {onModuleDueDate && (
          <MenuItem
            onClick={() => {
              if (menuModuleIdx !== null) onModuleDueDate(menuModuleIdx);
              setMenuAnchor(null);
              setMenuModuleIdx(null);
            }}
            sx={{ fontSize: 13, fontFamily: 'Inter', color: 'var(--Text-text-primary, #FDFDFD)' }}
          >
            Add Start &amp; End Date
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            if (menuModuleIdx !== null) onDeleteModule(menuModuleIdx);
            setMenuAnchor(null);
            setMenuModuleIdx(null);
          }}
          sx={{ fontSize: 13, fontFamily: 'Inter', color: 'var(--UI-Error, #F44336)' }}
        >
          Delete
        </MenuItem>
      </Menu>

      {/* Lesson context menu */}
      <Menu
        anchorEl={lessonMenuAnchor}
        open={Boolean(lessonMenuAnchor)}
        onClose={() => {
          setLessonMenuAnchor(null);
          setLessonMenuTarget(null);
        }}
        PaperProps={{ sx: { background: '#1A1A1A', border: '1px solid var(--Stroke-Border-border-dim, #2A2A2A)' } }}
      >
        <MenuItem
          onClick={() => {
            if (lessonMenuTarget) {
              const lesson = modules[lessonMenuTarget.m]?.lessons[lessonMenuTarget.l];
              openRenameModal('lesson', lessonMenuTarget.m, lessonMenuTarget.l, lesson?.title);
            }
            setLessonMenuAnchor(null);
            setLessonMenuTarget(null);
          }}
          sx={{ fontSize: 13, fontFamily: 'Inter', color: 'var(--Text-text-primary, #FDFDFD)' }}
        >
          Rename
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (lessonMenuTarget) onDeleteLesson(lessonMenuTarget.m, lessonMenuTarget.l);
            setLessonMenuAnchor(null);
            setLessonMenuTarget(null);
          }}
          sx={{ fontSize: 13, fontFamily: 'Inter', color: 'var(--UI-Error, #F44336)' }}
        >
          Delete
        </MenuItem>
      </Menu>

      {/* Rename modal */}
      <Dialog
        open={!!renameModal}
        onClose={() => {
          setRenameModal(null);
          setRenameValue('');
        }}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            background: '#1A1A1A',
            borderRadius: '12px',
            border: '1px solid var(--Stroke-Border-border-dim, #2A2A2A)',
            p: 3
          }
        }}
      >
        {renameModal && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Typography
              sx={{ fontSize: 18, fontWeight: 700, fontFamily: 'Inter', color: 'var(--Text-text-primary, #FDFDFD)' }}
            >
              Rename {renameModal.type === 'module' ? 'Module' : 'Lesson'}
            </Typography>
            <Box>
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 600,
                  fontFamily: 'Inter',
                  color: 'var(--Text-text-secondary, #B6B6B6)',
                  mb: 1
                }}
              >
                Name
              </Typography>
              <Box
                component="input"
                autoFocus
                value={renameValue}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setRenameValue(e.target.value)}
                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                  if (e.key === 'Enter') handleRenameSubmit();
                }}
                sx={{
                  width: '100%',
                  background: 'transparent',
                  border: '1px solid var(--Stroke-Border-border-dim, #2A2A2A)',
                  borderRadius: '8px',
                  px: 1.5,
                  py: 1,
                  color: 'var(--Text-text-primary, #FDFDFD)',
                  fontFamily: 'Inter',
                  fontSize: 14,
                  outline: 'none',
                  '&:focus': { borderColor: 'var(--Stroke-highlight, #F79420)' }
                }}
              />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
              <Box
                component="button"
                onClick={() => {
                  setRenameModal(null);
                  setRenameValue('');
                }}
                sx={{ ...secondaryButtonSx, cursor: 'pointer' }}
              >
                Cancel
              </Box>
              <Box
                component="button"
                onClick={handleRenameSubmit}
                disabled={!renameValue.trim()}
                sx={{ ...ctaButtonSx, border: 'none', cursor: renameValue.trim() ? 'pointer' : 'not-allowed' }}
              >
                Save
              </Box>
            </Box>
          </Box>
        )}
      </Dialog>
    </Box>
  );
};

export default ModuleSidebar;
